import { SCHEDULE_SESSION_DISPLAY_TYPE_ROUND, SCHEDULE_SESSION_DISPLAY_TYPE_SPEEDDATE } from 'common-data-handling';
import _, { escapeRegExp } from 'lodash';
import set from 'lodash/fp/set';
import moment from 'moment';

// TODO: remove filter from here: it can be stored elsewhere

const EMPTY_FILTER = {
    text: null,
    categories: {},
};

export default function (state, action) {
    if (!state) {
        state = {
            loaded: false,
            fetchTime: null,
            //
            all: [],
            filterable: [],
            filtered: [], // program items after all filters applied
            visible: [], // filtered items + always visible items
            indexed: {},
            categories: [],
            //
            filter: _.clone(EMPTY_FILTER),
            recommendedFilter: null,
            isRecommendedFilter: false,
            //
            sessions: {},
            speeddates: {},
        };
    }

    let previous = state;

    // apply action ------------------------------------------------------------
    switch (action.type) {
        case 'EVENT_PROGRAM_LOADED':
            state = Object.assign({}, state, {
                fetchTime: moment(action.fetchTime),
                loaded: true,
                all: [...action.program],
                categories: action.categories || [],
                recommendedFilter: action.recommendedFilter?.length ? action.recommendedFilter : null,
                isRecommendedFilter: false,
            });
            break;

        case 'EVENT_PROGRAM_SELECTION_UPDATE':
            state = set(
                'all',
                state.all.map((item) => {
                    // If we can, update whole item
                    if (action.updatedItem && item.id == action.updatedItem.id) {
                        return Object.assign({}, item, action.updatedItem);
                    }
                    if (_(action.selectedItems).includes(item.id)) {
                        return Object.assign({}, item, { is_selected: true });
                    } else if (_(action.deselectedItems).includes(item.id)) {
                        return Object.assign({}, item, { is_selected: false });
                    }
                    return item;
                }),
                state
            );
            break;
        case 'EVENT_PROGRAM_UPDATE':
            state = {
                ...state,
                all: state.all.map((item) => (item.id == action.programId ? { ...item, ...action.data } : item)),
            };
            break;
        case 'EVENT_PROGRAM_SET_FILTER':
            state = set('filter', action.filter, state);
            state = set('isRecommendedFilter', false, state);
            break;
        case 'EVENT_PROGRAM_SET_FILTER_TEXT':
            state = set('filter.text', action.text, state);
            break;
        case 'EVENT_PROGRAM_ADD_FILTER_CATEGORY_OPTION':
            state = set(`filter.categories[${action.category}]`, _.uniq([...(state.filter.categories[action.category] || []), action.option]), state);
            state = set('isRecommendedFilter', false, state);
            break;
        case 'EVENT_PROGRAM_REMOVE_FILTER_CATEGORY_OPTION':
            state = set(
                `filter.categories[${action.category}]`,
                _.reject(state.filter.categories[action.category] || [], (option) => option == action.option),
                state
            );
            state = set('isRecommendedFilter', false, state);
            break;
        case 'EVENT_PROGRAM_SET_FILTER_CATEGORIES':
            state = set('filter.categories', action.filterCategories, state);
            state = set('isRecommendedFilter', false, state);
            break;
        case 'EVENT_PROGRAM_CLEAR_FILTER_CATEGORIES':
            state = set('filter.categories', {}, state);
            state = set('isRecommendedFilter', false, state);
            break;
        case 'EVENT_PROGRAM_CLEAR_FILTER':
            state = set('filter', _.clone(EMPTY_FILTER), state);
            state = set('isRecommendedFilter', false, state);
            break;
        case 'EVENT_PROGRAM_APPLY_RECOMMENDED_FILTER':
            if (state.recommendedFilter) {
                state = set(
                    'filter.categories',
                    _(state.recommendedFilter).reduce((filter, category) => {
                        filter[category.name] = category.options;
                        return filter;
                    }, {}),
                    state
                );
                state = set('isRecommendedFilter', true, state);
            }
            break;
    }

    // TODO: move all this to memoized hooks
    // propagate changes for auto calculated/filtered properties ---------------
    if (previous != state) {
        // refilter
        if (previous.filter != state.filter || previous.all != state.all) {
            // filter out items that shouldn't be hidden by the filter
            state.filterable = state.filtered = _(state.all)
                // .reject(item => !item.categories || !item.categories.length)
                // .reject(item => item.display_type == 'round')
                .value();

            // filter by text
            if (state.filter.text) {
                _(state.filter.text.split(/\W/))
                    .map((word) => word.trim())
                    .compact()
                    .each((word) => {
                        let re = new RegExp(escapeRegExp(word), 'i'); // new RegExp(word, 'i');
                        state.filtered = _(state.filtered)
                            .filter((item) => item.name.match(re) || item.description.match(re))
                            .value();
                    });
            }

            // filter by categories
            if (_(state.filter.categories).values().flatten().value().length) {
                _(state.filter.categories)
                    .keys()
                    .each((category) => {
                        if (state.filter.categories[category].length) {
                            state.filtered = _(state.filtered)
                                .filter((item) => {
                                    if (item._ignoreFilter) {
                                        return true;
                                    }

                                    let itemCategories = _(item)
                                        .chain()
                                        .get('categories', [])
                                        .find((cat) => cat.name == category)
                                        .get('options', [])
                                        .value();
                                    return _.intersection(itemCategories, state.filter.categories[category]).length;
                                })
                                .value();
                        }
                    });
            }

            // Visible items: filtered + not filterable + selected items
            // deprecated: this should be removed
            // use isVisibleScheduleItem filter when needed
            state.visible = _([
                ...state.filtered,
                ..._(state.all)
                    .filter((item) => !item.categories || !item.categories.length || (item.is_selectable && item.is_selected) || item.display_type == SCHEDULE_SESSION_DISPLAY_TYPE_ROUND)
                    .value(),
            ])
                .uniq()
                .sortBy((item) => [item.start, (item) => item.order])
                .value();
        }

        // indexed
        if (previous.all != state.all) {
            state.indexed = _(state.all).reduce((indexed, item) => {
                indexed[item.id] = item;
                return indexed;
            }, {});
        }

        // sessions
        if (state.filtered != previous.filtered) {
            state.sessions = _(state.filtered).reject({ display_type: SCHEDULE_SESSION_DISPLAY_TYPE_ROUND }).value();
        }

        // speeddates
        if (state.filtered != previous.filtered) {
            state.speeddates = _(state.filtered)
                .filter({ display_type: SCHEDULE_SESSION_DISPLAY_TYPE_SPEEDDATE })
                .reduce((speeddates, item) => {
                    speeddates[item.id] = item;
                    return speeddates;
                }, {});
        }
    }

    return state;
}
